import type { GameId } from "@bing/shared";
import type { ReactNode } from "react";
import { GAME_ART } from "../assets/game-art";
import type { FriendView } from "../api/client";
import { GAMES, type GameInfo } from "../config";
import { fa } from "../i18n/fa";
import { useSession } from "../store/context";
import { Avatar } from "./Avatar";
import { displayName } from "./profile-ui";
import { Sheet } from "./social-ui";

/**
 * The «دعوت به بازی» bottom sheet on a friend row: pick which game to play.
 * Picking a game hands off to `inviteFriendToGame` (session store), which
 * host-creates the private table with the default rules — the roomId is only
 * born there — and the waiting room's invite panel then auto-sends the invite
 * to this friend. Closing without picking sends nothing.
 *
 * The tile list is the SAME source of truth as the lobby grid — the session
 * store's `lobbyGames` (`GET /games`, already sorted by the server's `order`,
 * which is the index into the canonical `LOBBY_GAME_ORDER`; the store falls back
 * to that order while the request is in flight). It is NOT the static `GAMES`
 * catalogue's declaration order, which is a different sequence (hearts first) and
 * was what this sheet used to iterate — so «حکم» and «بی‌دل» came out swapped
 * versus the lobby. The catalogue is now consulted only for a game's art/label.
 *
 * Activation mirrors the lobby exactly: a COMING_SOON game is `locked` for
 * everyone except admins, and a locked game is OMITTED here — picking it would
 * host a private table the game-server's `GameGate` refuses to create. Admins
 * (who bypass that gate) still get the tile, badged «بزودی» like the lobby, so
 * they can test it with a friend (private JOIN is exempt from the gate).
 */
export function GameInviteSheet({
  friend,
  onPick,
  onClose,
}: {
  friend: FriendView;
  onPick: (game: GameId) => void;
  onClose: () => void;
}): ReactNode {
  const lobbyGames = useSession((s) => s.lobbyGames);
  const isAdmin = useSession((s) => s.isAdmin);
  const title = fa.invite.sheetTitle(displayName(friend));

  // Canonical order in, catalogue art out. A game the catalogue doesn't know yet
  // is skipped (same guard the lobby uses), as is one this user cannot launch.
  const tiles = lobbyGames.flatMap((entry) => {
    const locked = entry.status === "COMING_SOON" && !isAdmin;
    if (locked) return [];
    const info: GameInfo | undefined = GAMES.find((g) => g.id === entry.game);
    return info ? [{ game: entry.game, info, comingSoon: entry.status === "COMING_SOON" }] : [];
  });

  return (
    <Sheet label={title} onClose={onClose}>
      <div className="mb-4 flex items-center gap-sm">
        <Avatar
          avatarId={friend.avatarId}
          avatarPhotoId={friend.avatarPhotoId}
          size={40}
          ringClass="border-gold-deep shadow-md"
          ringWidth={1}
        />
        <div className="min-w-0">
          <h3 className="m-0 truncate font-headline-sm text-headline-sm text-gold-deep">{title}</h3>
          <p className="m-0 font-caption text-caption text-on-surface-variant">
            {fa.invite.pickGame}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {tiles.map(({ game, info, comingSoon }) => (
          <button
            key={game}
            type="button"
            aria-label={info.fa}
            onClick={() => onPick(game)}
            className="group flex cursor-pointer appearance-none flex-col overflow-hidden rounded-xl border border-outline-variant bg-surface-container-high text-right transition-colors hover:border-gold-deep/60 hover:bg-surface-bright active:scale-[0.98]"
          >
            <span className="relative block h-16 w-full overflow-hidden">
              <img
                src={GAME_ART[game]}
                alt=""
                aria-hidden="true"
                className="h-full w-full object-cover transition-transform group-hover:scale-105"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-surface-container-high/85 to-transparent" />
              <span aria-hidden="true" className="absolute bottom-1 right-2 text-xl leading-none">
                {info.icon}
              </span>
              {/* Admin-only (a locked coming-soon tile never renders) — the same
                  plain «بزودی» badge the lobby shows, so an admin testing an
                  unreleased game sees its public state. */}
              {comingSoon ? (
                <span className="absolute left-1.5 top-1.5 flex items-center rounded-full border border-gold-deep/40 bg-felt-deep/90 px-2 py-0.5 font-caption text-caption text-gold-deep shadow-sm">
                  {fa.lobby.comingSoon}
                </span>
              ) : null}
            </span>
            <span className="flex min-w-0 flex-col p-3">
              <span className="truncate font-body-lg text-body-lg text-on-surface">{info.fa}</span>
              <span className="font-caption text-caption text-on-surface-variant">
                {info.players} {fa.lobby.players}
              </span>
            </span>
          </button>
        ))}
      </div>
    </Sheet>
  );
}
